import React, { useEffect, useMemo, useState } from "react";
import Header from "../../layout/Header";
import { fetchAPI } from "../../service/Encrtyption";
import { useDispatch, useSelector } from "react-redux";
import { Loader } from "../../loader";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { claimStatus, myRewards, reclaimDataSave, rewardMoney } from "../../auth/redux/store";
import ReactGA from 'react-ga4';
import { Button } from "react-bootstrap";

/** Backend may omit lists, send null, a single object, PascalCase keys, or { list/items: [...] }. */
const asRewardsList = (value) => {
    if (value == null || value === "") return [];
    if (Array.isArray(value)) return value;

    const inner =
        value.list ??
        value.items ??
        value.data ??
        value.claimDTOList ??
        value.successDTOList;

    if (Array.isArray(inner)) return inner;
    if (inner != null && typeof inner === "object") return [inner];

    if (typeof value === "object" && !Array.isArray(value)) return [value];
    return [];
};

const listsFromCashFree = (cashFreeTransactionList) => {
    if (!cashFreeTransactionList || typeof cashFreeTransactionList !== "object") {
        return { dsrClaimList: [], dsrSuccessList: [], dsrReClaimList: [] };
    }
    const c = cashFreeTransactionList;
    return {
        dsrClaimList: asRewardsList(c.dsrClaimList ?? c.dsrClaimList),
        dsrSuccessList: asRewardsList(c.dsrSuccessList ?? c.dsrSuccessList),
        dsrReClaimList: asRewardsList(
            c.dsrReClaimList ?? c.dsrReClaimList ?? c.dsrReClaimList ?? c.dsrReClaimList
        ),
    };
};

/** V2: lists live on dsrTransactionList; cashFree often has program meta only — do not concat both or amounts double. */
const pickRewardsList = (fromCash, fromSeed) =>
    fromSeed.length > 0 ? fromSeed : fromCash;

const listsBundleFromApiPayload = (payload) => {
    if (payload === false || payload == null || typeof payload !== "object") {
        return { dsrClaimList: [], dsrSuccessList: [], dsrReClaimList: [] };
    }
    const fromCash = listsFromCashFree(payload.cashFreeTransactionList);
    const st = payload.dsrTransactionList ?? payload.dsrTransactionList;
    const fromSeed = {
        dsrClaimList: asRewardsList(st?.dsrClaimList ?? st?.dsrClaimList),
        dsrSuccessList: asRewardsList(st?.dsrSuccessList ?? st?.dsrSuccessList ?? st?.dsrSuccessList),
        dsrReClaimList: asRewardsList(
            st?.dsrReClaimList ?? st?.dsrReClaimList ?? st?.dsrReClaimList ?? st?.dsrReClaimList
        ),
    };
    return {
        dsrClaimList: pickRewardsList(fromCash.dsrClaimList, fromSeed.dsrClaimList),
        dsrSuccessList: pickRewardsList(fromCash.dsrSuccessList, fromSeed.dsrSuccessList),
        dsrReClaimList: pickRewardsList(fromCash.dsrReClaimList, fromSeed.dsrReClaimList),
    };
};

const sumListAmounts = (rows) =>
    (Array.isArray(rows) ? rows : []).reduce(
        (total, row) =>
            total + (parseFloat(row.amount ?? row.cashRewards) || 0),
        0
    );

/** Header card: API lifetime total from dsrTransactionList (or cashFree fallback). */
const getApiTotalCashbackWon = (seedMsg, cfsMsg, data) => {
    const raw =
        seedMsg?.totalCashbackWon ??
        seedMsg?.TotalCashbackWon ??
        cfsMsg?.totalCashbackWon ??
        data?.totalCashbackWon;
    const apiNum = parseFloat(raw);
    if (!Number.isNaN(apiNum) && apiNum >= 0) return apiNum;
    return null;
};

const getApiPacketsScannedCount = (seedMsg, cfsMsg, data) => {
    const raw =
        seedMsg?.packetsScannedCount ??
        data?.packetsScannedCount ??
        data?.totalStrongarmPacketsScanned ??
        cfsMsg?.packetsScanned;
    if (raw != null && String(raw).trim() !== "") return raw;
    return null;
};

const formatCashbackAmount = (value) => {
    if (value == null) return "—";
    const n = Number(value);
    if (!Number.isFinite(n)) return "—";
    return n % 1 === 0 ? String(n) : n.toFixed(2);
};

const DEFAULT_REWARD_TAB = "dsrSuccessList";
const REWARD_TAB_IDS = ["dsrSuccessList", "dsrClaimList", "dsrReClaimList"];

export const GiftHistory = () => {

    const styles = {
        page: {
            display: "flex",
            flexDirection: "column",
            alignItems: "stretch",
            width: "100%",
            maxWidth: "100%",
            marginTop: "0.75rem",
            padding: "0 8px",
            boxSizing: "border-box",
        },

        panel: {
            width: "100%",
            maxWidth: "100%",
            boxShadow: "0 4px 15px rgba(0,0,0,0.15)",
            background: "#fff",
            borderRadius: "12px",
            overflow: "hidden",
        },

        tableWrap: {
            width: "100%",
            maxWidth: "100%",
            overflowX: "hidden",
            padding: "12px",
            boxSizing: "border-box",
        },

        emptyCell: {
            padding: "20px 12px",
            textAlign: "center",
            border: "2px solid #5ecb74",
            color: "#666",
            fontSize: "13px",
            wordBreak: "break-word",
        },

        content: {
            width: "100%",
            background: "#fff",
        },

        table: {
            width: "100%",
            maxWidth: "100%",
            tableLayout: "fixed",
            borderCollapse: "collapse",
            textAlign: "center",
            fontSize: "12px",
        },

        th: {
            background: "#2BB24B",
            color: "#fff",
            padding: "10px 6px",
            border: "2px solid #5ecb74",
            fontWeight: "bold",
            wordBreak: "break-word",
        },

        td: {
            padding: "8px 6px",
            border: "2px solid #5ecb74",
            wordBreak: "break-word",
            overflowWrap: "anywhere",
        },
    };

    const { t, i18n } = useTranslation();
    const { lang } = useParams();
    const currentLanguage = lang;
    const obj = {
        cashFreeTransactionId: 0,
        dsrId: 0,
        ecouponFarmerMapId: 0,
        moduleType: "Farmer",
        programId: 0,
        reClaim: "No",
        showView: false,
        countOfToBeClaims: 0,
        dsrProgramId: 0,
        ecouponFarmerId: 0,
        enableSprayService: false,
        isCortevaProduct: false,
        isLumivia: false,
        isProductAlreadyScaned: false,
        isSpinDone: false,
        lumiviaServReqRecordID: 0,
        multipleRewards: false,
        seadProgramId: 0,
        showClickableLink: false,
        showLuckyWinnerPopup: false,
        showRetailerIdPopup: false,
        uq_responseCode: 0,
        wheelStoppingIndex: 0,
        destinationURL: "/cashFreeReward/getCashFreeTransactionPendingRequest_V2",
        appLangCode: currentLanguage
    };

    const [formData, setFormData] = useState(obj);
    const [programName, setProgramName] = useState('');
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState(false);
    const user = useSelector((state) => state?.user);
    const amount = useSelector((state) => state?.amount);
    const [errroMsg, setErrorMsg] = useState("");
    const browserType = sessionStorage.getItem("browserName") || "web";


    const location = useLocation();
    const [activeTab, setActiveTab] = useState(() => {
        const fromNav = location.state?.defaultTab;
        return REWARD_TAB_IDS.includes(fromNav) ? fromNav : DEFAULT_REWARD_TAB;
    });


    const mobileNumber = sessionStorage.getItem('mbn');

    const dispatch = useDispatch();

    const navigate = useNavigate();

    const hide = () => {
        setErrorMsg("");
    };

    const onSubmit = async () => {
        // debugger;
        setLoading(true);
        console.log(JSON.stringify(formData) + "requestdata for claimlist");

        if (!mobileNumber) {
            setErrorMsg(t("sessionExperied"));
            setTimeout(() => {
                navigate(`/${lang}/giftHistory`);
            }, 2000);
            return;
        }

        try {
            const responseData = await fetchAPI(
                process.env.REACT_APP_API_URL,
                formData,
                {
                    mobileNumber: mobileNumber,
                    appLangCode: currentLanguage,
                    deviceType: "web",
                    browserType: browserType

                }
            );
            const jsonReposneData = JSON.parse(responseData);
            const programTitle = jsonReposneData.cashFreeTransactionList?.programTitle ?? jsonReposneData.dsrTransactionList?.programTitle;
            console.log(JSON.stringify(jsonReposneData) + "jsonReposneData");
            setProgramName(programTitle);
            console.log(programTitle + "programName");
            setData(jsonReposneData);
            dispatch(myRewards(jsonReposneData));
            // console.log(JSON.stringify(data.eCouponTransactionList.ecouponCodeList) + "niharika");
        } catch (error) {
            console.log(error);
        } finally {
            setLoading(false); // Set loading state to false after API call is completed
        }
    };
    useEffect(() => {
        onSubmit();
    }, []);

    const handleClaimSubmit = () => {
        ReactGA.event({
            category: 'User Register',
            action: 'CLK_CLAIM_BTN'
        });

        if (claimPayoutAmount != null) {
            dispatch(rewardMoney(claimPayoutAmount));
        }
        dispatch(claimStatus(true));
        navigate(`/${lang}/paymentmode`);
    };

    const handleReClaimSubmit = (sucess) => {
        // debugger; 
        ReactGA.event({
            category: 'User Register',
            action: 'CLK_RECLAIM_BTN'
        });
        // debugger;
        dispatch(claimStatus(false));
        dispatch(reclaimDataSave(sucess));
        dispatch(rewardMoney(sucess.amount));
        navigate(`/${lang}/paymentmode?isreclaim=true`);
    };


    useEffect(() => {
        if (!data || typeof data !== "object") return;
        const { dsrClaimList: claims } = listsBundleFromApiPayload(data);
        const claimSum = sumListAmounts(claims);
        if (claimSum > 0) {
            dispatch(rewardMoney(claimSum));
        }
    }, [data, dispatch]);

    const fmtAmount = (val) =>
        val != null && !isNaN(parseFloat(val)) && isFinite(val) ? `₹${val}` : val ?? "—";

    const listBundle = useMemo(
        () => listsBundleFromApiPayload(data),
        [data]
    );

    const { dsrClaimList, dsrSuccessList, dsrReClaimList } = listBundle;

    const hasRewardsEnvelope = useMemo(() => {
        if (!data || typeof data !== "object") return false;
        return (
            data.cashFreeTransactionList != null ||
            data.dsrTransactionList != null ||
            data.dsrTransactionList != null
        );
    }, [data]);

    const rewardTabs = useMemo(
        () => [
            { id: "dsrSuccessList", label: t("successList"), source: "success" },
            { id: "dsrClaimList", label: t("claimList"), source: "claim" },
            { id: "dsrReClaimList", label: t("reclaimList"), source: "reclaim" },
        ],
        [t]
    );

    useEffect(() => {
        if (!hasRewardsEnvelope) return;
        const fromNav = location.state?.defaultTab;
        setActiveTab(
            REWARD_TAB_IDS.includes(fromNav) ? fromNav : DEFAULT_REWARD_TAB
        );
    }, [hasRewardsEnvelope, location.state?.defaultTab]);

    const resolvedActiveTab =
        rewardTabs.find((tab) => tab.id === activeTab)?.id ??
        rewardTabs[0]?.id ??
        DEFAULT_REWARD_TAB;

    const cfsMsg = data?.cashFreeTransactionList;
    const seedMsg =
        typeof data === "object" && data
            ? data.dsrTransactionList ?? data.dsrTransactionList
            : null;

    const summaryPrimary = String(
        cfsMsg?.primaryMessage ??
        cfsMsg?.PrimaryMessage ??
        seedMsg?.primaryMessage ??
        seedMsg?.PrimaryMessage ??
        data?.primaryMessage ??
        data?.PrimaryMessage ??
        "",
    ).trim();

    /** Header: API totalCashbackWon (e.g. 100.00). Claim button: sum of dsrClaimList only (e.g. 55). */
    const totalCashbackWon = useMemo(() => {
        if (!data || typeof data !== "object") return "—";
        return formatCashbackAmount(getApiTotalCashbackWon(seedMsg, cfsMsg, data));
    }, [cfsMsg, data, seedMsg]);

    const claimPayoutAmount = useMemo(() => {
        const sum = sumListAmounts(dsrClaimList);
        return sum > 0 ? sum : null;
    }, [dsrClaimList]);

    const packetsScannedDisplay = useMemo(() => {
        if (!data || typeof data !== "object") return "—";
        const raw = getApiPacketsScannedCount(seedMsg, cfsMsg, data);
        return raw != null ? raw : "—";
    }, [cfsMsg, data, seedMsg]);

    const currentTab =
        rewardTabs.find((tab) => tab.id === resolvedActiveTab) ?? rewardTabs[0];

    const currentRowCount =
        currentTab?.source === "claim"
            ? dsrClaimList.length
            : currentTab?.source === "success"
                ? dsrSuccessList.length
                : dsrReClaimList.length;

    /** Table date: DD-MM-YY with 2-digit year (e.g. 24-04-26). */
    const formatDdMmYy = (ts) => {
        if (ts == null || ts === "") return "—";
        const d = new Date(ts);
        if (Number.isNaN(d.getTime())) return "—";
        const day = String(d.getDate()).padStart(2, "0");
        const month = String(d.getMonth() + 1).padStart(2, "0");
        const yy = String(d.getFullYear()).slice(-2);
        return `${day}-${month}-${yy}`;
    };

    const isRowRedeemed = (row) =>
        String(row?.reClaim ?? "").toLowerCase() === "yes" ||
        String(row?.status ?? "").toLowerCase() === "yes" ||
        row?.flag === true;

    const renderRedeemedCell = (row) =>
        isRowRedeemed(row) ? t("yes") : t("no");

    const giftHistoryList =
        data?.eCouponTransactionList?.ecouponCodeList?.filter(
            (item) => item.valueThree
        ) || [];

    return (
        <>
            <Header />
            <div className="main">

                <div className="container">
                    {loading && <Loader />}

                    <div className="text-center mt-4">

                        <h5 className="card-title">{t('scanned_products')}</h5>

                        <div className="d-flex justify-content-center mt-4" style={{ gap: ".5rem" }}>
                            {data?.eCouponTransactionList?.ecouponCodeList?.length > 0 ? (
                                <>
                                    {/* Scan Date Card */}
                                    <div className="card w-50 shadow giftHistoryCard">
                                        <div className="card-header giftHistoryCardHeader bg-success text-white text-center">
                                            {t("scan_date")}
                                        </div>

                                        <div className="card-body giftHistoryCardBody p-2">

                                            {/* {data.eCouponTransactionList.ecouponCodeList.map((item, index) => (
                                                <div key={index} className="text-center">
                                                    {item.valueTwo}
                                                </div>
                                            ))} */}

                                            {data.eCouponTransactionList.ecouponCodeList.map((item, index) => {
                                                const date = new Date(item.valueTwo);

                                                const formattedDateTime = `${String(date.getDate()).padStart(2, "0")}-${String(
                                                    date.getMonth() + 1
                                                ).padStart(2, "0")}-${date.getFullYear()} ${String(
                                                    date.getHours()
                                                ).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`;

                                                return (
                                                    <div key={index} className="text-center">
                                                        {formattedDateTime ? formattedDateTime : "—"}
                                                    </div>
                                                );
                                            })}

                                        </div>
                                    </div>

                                    {/* Packet ID Card */}
                                    <div className="card w-50 shadow giftHistoryCard">
                                        <div className="card-header giftHistoryCardHeader bg-success text-white text-center">
                                            {t("packet_id")}
                                        </div>

                                        <div className="card-body giftHistoryCardBody text-left p-2 pl-4">
                                            {data.eCouponTransactionList.ecouponCodeList.map((item, index) => (
                                                <div key={index}>
                                                    {item.valueOne}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </>
                            ) : (
                                <div className="text-center w-100 py-3">
                                    <h6 className="text-muted">
                                        {t("noData") || "No Data Available"}
                                    </h6>
                                </div>
                            )}
                        </div>

                        {giftHistoryList.length > 0 && (
                            <div className="text-center mt-4">
                                <h5 className="card-title">{t('gift_history')}</h5>

                                <div className="d-flex justify-content-center mt-4" style={{ gap: ".5rem" }}>

                                    <div className="card shadow giftHistoryCard" style={{ width: "33%" }}>
                                        <div className="card-header giftHistoryCardHeader bg-success text-white text-center">
                                            {t('reward_win_date')}
                                        </div>
                                        <div className="card-body giftHistoryCardBody p-2">
                                            {/* {giftHistoryList.map((item, index) => (
                                                <div key={index} className="text-center">
                                                    {item.valueTwo}
                                                </div>
                                            ))} */} 

                                            {giftHistoryList.map((item, index) => {
                                                const date = new Date(item.valueTwo);
                                                const formattedDateTime = `${String(date.getDate()).padStart(2, "0")}-${String(
                                                    date.getMonth() + 1
                                                ).padStart(2, "0")}-${date.getFullYear()} ${String(
                                                    date.getHours()
                                                ).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`;

                                                return (
                                                    <div key={index} className="text-center">
                                                        {formattedDateTime ? formattedDateTime : "—"}
                                                    </div>
                                                );
                                            })}

                                        </div>
                                    </div>

                                    <div className="card shadow giftHistoryCard" style={{ width: "33%" }}>
                                        <div className="card-header giftHistoryCardHeader bg-success text-white text-center">
                                            {t('reward')}
                                        </div>
                                        <div className="card-body giftHistoryCardBody p-2">
                                            {giftHistoryList.map((item, index) => (
                                                <div key={index} className="text-center">
                                                    {item.valueThree}
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    <div className="card shadow giftHistoryCard" style={{ width: "33%" }}>
                                        <div className="card-header giftHistoryCardHeader bg-success text-white text-center">
                                            {t('status')}
                                        </div>
                                        <div className="card-body giftHistoryCardBody p-2">
                                            {giftHistoryList.map((item, index) => (
                                                <div key={index} className="text-center">
                                                    {/* {item.status} */}
                                                    {t('claimed')}
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                </div>
                            </div>
                        )}

                        {/* {data?.eCouponTransactionList?.ecouponCodeList?.length > 0 && (
                            <div className="text-center mt-4">
                                <h5 className="card-title">{t('gift_history')}</h5>

                                <div className="d-flex justify-content-center mt-4" style={{ gap: ".5rem" }}>

                                    <div className="card shadow giftHistoryCard" style={{ width: "33%" }}>
                                        <div className="card-header giftHistoryCardHeader bg-success text-white text-center">
                                            {t('reward_win_date')}
                                        </div>
                                        <div className="card-body giftHistoryCardBody p-2">
                                            {data.eCouponTransactionList.ecouponCodeList.map((item, index) => (
                                                <div key={index} className="text-center">
                                                    {item.valueTwo} </div>
                                            ))}
                                        </div>
                                    </div>

                                    <div className="card shadow giftHistoryCard" style={{ width: "33%" }}>
                                        <div className="card-header giftHistoryCardHeader bg-success text-white text-center">
                                            {t('reward')}
                                        </div>
                                        <div className="card-body giftHistoryCardBody p-2">
                                            {data.eCouponTransactionList.ecouponCodeList.map((item, index) => (
                                                <div key={index} className="text-center"> {item.valueThree} </div>
                                            ))}
                                        </div>
                                    </div>

                                    <div className="card shadow giftHistoryCard" style={{ width: "33%" }}>
                                        <div className="card-header giftHistoryCardHeader bg-success text-white text-center">
                                            {t('status')}
                                        </div>
                                        <div className="card-body giftHistoryCardBody p-2">
                                            {data.eCouponTransactionList.ecouponCodeList.map((item, index) => (
                                                <div key={index} className="text-center"> {item.status} </div>
                                            ))}
                                        </div>
                                    </div>

                                </div>

                            </div>
                        )} */}

                        <div className="d-flex flex-direction-column bottomBtns" style={{ gap: '1rem' }}>
                            <Button className="m-auto rounded-4 py-2 btnshadow btn-secondary" style={{ width: '93%' }}
                                onClick={() => {
                                    ReactGA.event({
                                        label: 'User Scan More',
                                        category: 'User Scan More',
                                        action: 'Clicked Scan More Button - Gift History Page'
                                    });
                                    navigate(`/${lang}/scanProduct`)
                                }}>
                                {t("Scanmore")}
                            </Button>

                            {giftHistoryList.length > 0 && (

                                <Button className="m-auto rounded-4 py-2 btnshadow btn-secondary" style={{ width: '93%' }}
                                    onClick={() => {
                                        ReactGA.event({
                                            label: "Share Now Button",
                                            category: "User Share",
                                            action: "Clicked Share Now Button - Gift History Page",
                                        });
                                        navigate(`/${lang}/awardshare`)
                                    }}>

                                    {t('share')}
                                </Button>)}

                        </div>

                    </div>


                    {/* <div className="text-center scanSummaryCard">
                        <p className="mb-0">{t("awardSummary.totalPacketsScannedLabel")}</p>
                        {summaryPrimary ? (
                            <p
                                className="small text-muted px-3 mb-1 mt-1"
                                dangerouslySetInnerHTML={{
                                    __html: summaryPrimary.replace(/\r\n|\n|\r/g, "<br />"),
                                }}
                            />
                        ) : null}
                        <h2 className="font-extrabold">{packetsScannedDisplay}</h2>

                        <div className="totalAmountText">
                            {t("awardSummary.totalCashbackWonLabel")}
                        </div>

                        <h2 className="text-secondaryColor font-extrabold">₹ {totalCashbackWon}</h2>
                    </div> */}


                    {/* <div style={styles.page}>
                        {loading ? null : !hasRewardsEnvelope ? (
                            <p className="text-center py-4 mb-0 w-100">{t("noData")}</p>
                        ) : (
                            <div style={styles.panel}>
                                <div className="awardSummaryTabList" role="tablist">
                                    {rewardTabs.map((tab) => (
                                        <button
                                            key={tab.id}
                                            type="button"
                                            role="tab"
                                            className={`awardSummaryTabBtn${resolvedActiveTab === tab.id
                                                ? " awardSummaryTabBtnActive"
                                                : ""
                                                }`}
                                            aria-selected={resolvedActiveTab === tab.id}
                                            onClick={() => setActiveTab(tab.id)}
                                        >
                                            {tab.label}
                                        </button>
                                    ))}
                                </div>

                                <div style={styles.content}>
                                    {currentTab && (
                                        <>
                                            <div style={styles.tableWrap}>
                                                <table style={styles.table}>
                                                    <colgroup>
                                                        <col style={{ width: "26%" }} />
                                                        <col style={{ width: "28%" }} />
                                                        <col style={{ width: "22%" }} />
                                                        {currentTab.source !== "claim" && (<col style={{ width: "24%" }} />)}
                                                    </colgroup>
                                                    <thead>
                                                        <tr>
                                                            <th style={styles.th}>
                                                                {t("awardSummary.tableDateCol")}
                                                            </th>
                                                            <th style={styles.th}>
                                                                {currentTab.source === "claim" ? t("awardSummary.productnameLabel") : (t("awardSummary.tablePacketsScannedCol"))}
                                                            </th>

                                                            {currentTab.source !== "claim" && (
                                                                <th style={styles.th}>
                                                                    {t("awardSummary.tablePacketsScannedCol")}
                                                                </th>)}

                                                            <th style={styles.th}>
                                                                {t("awardSummary.tableCashbackCol")}
                                                            </th>
                                                            <th style={styles.th}>
                                                                {currentTab.source === "reclaim"
                                                                    ? t("status")
                                                                    : t("awardSummary.tableRedeemedCol")}
                                                            </th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {currentRowCount === 0 ? (
                                                            <tr>
                                                                <td
                                                                 
                                                                    colSpan={currentTab.source === "claim" ? 3 : 4}
                                                                    style={{
                                                                        ...styles.td,
                                                                        ...styles.emptyCell,
                                                                    }}
                                                                >
                                                                    {t("noTransactions", {
                                                                        defaultValue: t("awardSummary.noTransactionsDefault"),
                                                                    })}
                                                                </td>
                                                            </tr>
                                                        ) : (
                                                            <>
                                                                {currentTab.source === "claim" &&
                                                                    dsrClaimList.map((claim, index) => (
                                                                        <tr key={`claim-${index}`}>
                                                                            <td style={styles.td}>
                                                                                {formatDdMmYy(
                                                                                    claim.createdOn ??
                                                                                    claim.createdDate
                                                                                )}
                                                                            </td>
                                                                            
                                                                            <td style={styles.td}>
                                                                                {fmtAmount(
                                                                                    claim.amount ?? claim.cashRewards
                                                                                )}
                                                                            </td>
                                                                            <td style={styles.td}>
                                                                                {renderRedeemedCell(claim)}
                                                                            </td>
                                                                        </tr>
                                                                    ))}
                                                                {currentTab.source === "success" &&
                                                                    dsrSuccessList.map((row, index) => (
                                                                        <tr key={`success-${index}`}>
                                                                            <td style={styles.td}>
                                                                                {formatDdMmYy(
                                                                                    row.createdOn ??
                                                                                    row.createdDate
                                                                                )}
                                                                            </td>
                                                                            <td style={styles.td}>
                                                                                {row.referenceId ??
                                                                                    row.serverRecordId ??
                                                                                    "—"}
                                                                            </td>
                                                                            <td style={styles.td}>
                                                                                {fmtAmount(
                                                                                    row.amount ?? row.cashRewards
                                                                                )}
                                                                            </td>
                                                                            <td style={styles.td}>{t("yes")}</td>
                                                                        </tr>
                                                                    ))}
                                                                {currentTab.source === "reclaim" &&
                                                                    dsrReClaimList.map((row, index) => (
                                                                        <tr key={`reclaim-${index}`}>
                                                                            <td style={styles.td}>
                                                                                {formatDdMmYy(
                                                                                    row.createdOn ??
                                                                                    row.createdDate
                                                                                )}
                                                                            </td>
                                                                            <td style={styles.td}>
                                                                                {row.referenceId ??
                                                                                    row.serverRecordId ??
                                                                                    "—"}
                                                                            </td>
                                                                            <td style={styles.td}>
                                                                                {fmtAmount(
                                                                                    row.amount ?? row.cashRewards
                                                                                )}
                                                                            </td>
                                                                            <td style={styles.td}>
                                                                                <div className="d-flex flex-column gap-2 align-items-center">
                                                                                    <span>{row.status ?? "—"}</span>
                                                                                    <button
                                                                                        type="button"
                                                                                        onClick={() =>
                                                                                            handleReClaimSubmit(row)
                                                                                        }
                                                                                        className="btn btn-primary py-2 center-textbtn text-white claimBtn btn-sm w-100"
                                                                                        id={`reclaimBtn-${index}`}
                                                                                    >
                                                                                        {t("Reclaim")}
                                                                                    </button>
                                                                                </div>
                                                                            </td>
                                                                        </tr>
                                                                    ))}
                                                            </>
                                                        )}
                                                    </tbody>
                                                    {currentTab.source === "claim" &&
                                                        dsrClaimList.length > 0 && (
                                                            <tfoot>
                                                                <tr>
                                                                    <td
                                                                       
                                                                        colSpan={currentTab.source === "claim" ? 3 : 4}
                                                                        className="awardSummaryClaimFootCell"
                                                                    >
                                                                        <button
                                                                            type="button"
                                                                            onClick={handleClaimSubmit}
                                                                            className="btn btn-primary w-100 py-2 center-textbtn text-white claimBtn rounded-4 mb-0"
                                                                            id="submitBtn"
                                                                        >
                                                                            {t("Claim")} - ₹{" "}
                                                                            {formatCashbackAmount(
                                                                                claimPayoutAmount
                                                                            )}
                                                                        </button>
                                                                    </td>
                                                                </tr>
                                                            </tfoot>
                                                        )}
                                                </table>
                                            </div>
                                            <div className="awardSummaryShareSection">
                                                <button
                                                    type="button"
                                                    className="btn btn-primary bg-[#2BB24B] w-100 py-2 center-textbtn text-white claimBtn rounded-4 btnshadow"
                                                    onClick={() =>
                                                        navigate(`/${lang}/awardshare`)
                                                    }
                                                    id="shareRewardsBtn"
                                                >
                                                    {t("Share")}
                                                </button>
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>
                        )}
                    </div> */}




                    {/* <Accordion defaultActiveKey="0" style={{ paddingBottom: '4rem' }}>
            <Accordion.Item eventKey="0">
              <Accordion.Header>{programName}</Accordion.Header>
              {data ?
                <Accordion.Body>
                   <Accordion
                    activeKey={activeKeyAccordion1}
                    onSelect={handleAccordion1Toggle}
                  >
                    {data &&
                      data.cashFreeTransactionList &&
                      data.cashFreeTransactionList.claimList &&
                      data.cashFreeTransactionList.claimList.length > 0 ? (
                      <Accordion.Item eventKey="0">
                        <Accordion.Header>
                          {t("claimList")}

                          {activeKeyAccordion1 === "0" ? (
                            <FaChevronUp className="float-end" />
                          ) : (
                            <FaChevronDown className="float-end" />
                          )}
                        </Accordion.Header>
                        <Accordion.Body>
                          <div>
                            <div className="d-flex flex-column gap-4">
                              {data &&
                                data.cashFreeTransactionList &&
                                data.cashFreeTransactionList.claimList &&
                                data.cashFreeTransactionList.claimList.length >
                                0 ? (
                                data.cashFreeTransactionList.claimList.map(
                                  (claim, claimIndex) => (
                                    <div
                                      className="card"
                                      key={`claim-${claimIndex}`}
                                    >
                                      <div className="card-body p-2" style={{ fontSize: '.9rem' }}>
                                        {claim.productName && (<p className="mb-2">
                                          <span className="claimList-heading">
                                            {t("productName")}
                                          </span>
                                          : {claim.productName}
                                        </p>
                                        )}

                                        {claim.amount && (<p className="mb-2">
                                          <span className="claimList-heading">
                                            {t("reward")}
                                          </span>
                                          : ₹ {claim.amount}
                                        </p>
                                        )}

                                        {claim.createdOn && (<p className="mb-2 d-flex align-items-end" style={{ gap: '.2rem' }}>
                                          <span className="claimList-heading">
                                            {t("Date")}
                                          </span>
                                          : {new Date(claim.createdOn).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' }).replace(/,/g, '').split(' - ')}
                                        </p>
                                        )}

                                        {claim.prodSerialNumber && (<p className="mb-2 d-flex align-items-end" style={{ gap: '.2rem' }}>
                                          <span className="claimList-heading">
                                            {t("serialNO")}
                                          </span>
                                          : {claim.prodSerialNumber}
                                        </p>
                                        )}

                                      </div>
                                    </div>
                                  )
                                )
                              ) : (
                                <p> {t("noClaimsFound")}</p>
                              )}
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={handleClaimSubmit}
                            className="btn btn-primary w-100 py-2 center-textbtn mt-3 text-white claimBtn"
                            id="submitBtn"
                          >
                            {t("Claim")} - ₹
                            {amount}
                          </button>
                        </Accordion.Body>
                      </Accordion.Item>
                    ) : null}
                    {data &&
                      data.cashFreeTransactionList &&
                      data.cashFreeTransactionList.successList &&
                      data.cashFreeTransactionList.successList.length > 0 ? (
                      <Accordion.Item eventKey="1">
                        <Accordion.Header>
                          {t("successList")}
                          {activeKeyAccordion2 === "2" ? (
                            <FaChevronUp className="float-end" />
                          ) : (
                            <FaChevronDown className="float-end" />
                          )}
                        </Accordion.Header>
                        <Accordion.Body>
                          <div className="d-flex flex-column gap-4">
                            {data &&
                              data.cashFreeTransactionList &&
                              data.cashFreeTransactionList.successList &&
                              data.cashFreeTransactionList.successList.length > 0 ? (
                              data.cashFreeTransactionList.successList.map(
                                (success, successIndex) => (

                                  <div className="card" key={`success-${successIndex}`}>
                                    <div className="card-body p-2" style={{ fontSize: '.9rem' }}>
                                       {success.amount && (<p className="mb-2 d-flex">
                                        <span className="claimList-heading">
                                          {t("reward")}
                                        </span>
                                         : {
                                          !isNaN(parseFloat(success.amount)) &&
                                            isFinite(success.amount) ? `₹${success.amount}`
                                            : success.amount
                                        }
                                      </p>)}

                                      {success.referenceId && (<p className="mb-2">
                                        <span className="claimList-heading">
                                          {t("referenceId")}
                                        </span>
                                        : {success.referenceId}
                                      </p>
                                      )}

                                      {success.createdOn && (<p className="mb-2">
                                        <span className="claimList-heading">
                                          {t("Date")}
                                        </span>
                                        : {new Date(success.createdOn).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' }).replace(/,/g, '').split(' - ')}
                                       </p>
                                      )}

                                      {success.status && (<p className="mb-2">
                                        <span className="claimList-heading">
                                          {t("status")}
                                        </span>
                                        : {success.status}
                                      </p>)}
                                    </div>
                                  </div>

                                )
                              )
                            ) : (
                              <p> {t("noSuccessList")}</p>
                            )}
                          </div>
                        </Accordion.Body>
                      </Accordion.Item>
                    ) : null}

                    {data &&
                      data.cashFreeTransactionList &&
                      data.cashFreeTransactionList.reClaimList &&
                      data.cashFreeTransactionList.reClaimList.length > 0 ? (
                      <Accordion.Item eventKey="2">
                        <Accordion.Header>
                          {t("reclaimList")}
                          {activeKeyAccordion2 === "2" ? (
                            <FaChevronUp className="float-end" />
                          ) : (
                            <FaChevronDown className="float-end" />
                          )}
                        </Accordion.Header>
                        <Accordion.Body>
                          <div className="d-flex flex-column gap-4">

                            {data.cashFreeTransactionList.reClaimList.map((success, successIndex) => (
                              <div className="card" key={`success-${successIndex}`}>
                                <div className="card-body p-2" style={{ fontSize: '.9rem' }}>
                                  <FaInfoCircle className="infoImg" />

                                  {success.amount && (<p className="mb-2">
                                    <span className="claimList-heading">{t("reward")}</span>: ₹{success.amount}
                                  </p>)}

                                  {success.referenceId && (<p className="mb-2">
                                    <span className="claimList-heading">{t("referenceId")}</span>: {success.referenceId}
                                  </p>
                                  )}

                                  {success.createdOn & (<p className="mb-2">
                                    <span className="claimList-heading">{t("Date")}</span>: {formatDdMmYy(success.createdOn)}
                                  </p>)}

                                  {success.status && (<p className="mb-2">
                                    <span className="claimList-heading">{t("status")}</span>: {success.status}
                                  </p>)}
                                   <button
                                    type="button"
                                    onClick={() => handleReClaimSubmit(success)}
                                    className="btn btn-primary w-100 py-2 center-textbtn mt-3 text-white claimBtn"
                                    id={`submitBtn-${successIndex}`}
                                  >
                                    {t("Reclaim")}
                                  </button>
 
                                </div>
                              </div>
                            ))}
                          </div>
                        </Accordion.Body>
                      </Accordion.Item>
                    ) : (
                      null
                    )}
                  </Accordion>
                </Accordion.Body>

                : <p className="text-center pt-4"> {t("noData")} </p>}
            </Accordion.Item>
          </Accordion> */}

                    {errroMsg && (
                        <>
                            <div
                                className="alert alert-warning alert-dismissible fade show mt-2"
                                role="alert"
                            >
                                {errroMsg}
                                <button
                                    type="button"
                                    className="close"
                                    onClick={hide}
                                >
                                    <span aria-hidden="true">&times;</span>
                                </button>
                            </div>
                        </>
                    )}

                    {/* <button
            type="submit"
            className="btn btn-primary w-100 py-3
                  center-textbtn mt-3 text-white text-uppercase"
            id="submitBtn" style={{ position: 'fixed', bottom: '0', left: '0', borderRadius: '0' }}
            onClick={() => navigate(`/${lang}/home`)}
          >
            {t("BackToHome")}
          </button> */}
                </div>
            </div>
        </>
    );
};
